import { getAuthPort } from '@/data/auth';
import { config, PRODUTO_PADRAO } from '@/data/config';
import { CompilacoesHttpError } from '@/data/http';
import type {
  BuildDetalhe,
  BuildResumo,
  CompilacoesDataSource,
  EventoBuild,
  IncidenteResumo,
  Painel,
  PublicacaoPainel,
  Ramo,
  StatusBuild,
} from '@/data/types';
import { elegivelParaPainel, exigeMotivo, ROTULO_STATUS } from '@/lib/capabilities';
import { ordenarParaVersao } from '@/lib/incidentes';
import { calcularDiff } from '@/lib/diff';
import { trackRequest } from '@/lib/request-progress';
import type { BuildDemo, EstadoDemo } from './fixtures';
import { demoAdmin, resetDemoAdmin } from './admin';
import { agregadoDaVersao, criarIncidentesDemo } from './incidentes';
import {
  acessoDemo,
  colaboradorDoDemo,
  criarVisibilidadeDemo,
  exigirAcesso,
  proteger,
  resetDemoVisibilidade,
  visibilidadeDeRamos,
} from './visibilidade';
import { diaCivilSP } from '@/lib/periodo';

/**
 * Fonte de dados de demonstração.
 *
 * Mesma interface `CompilacoesDataSource` da fonte REST: nenhuma tela sabe qual
 * das duas está montada. As versões, links e manifestos são REAIS (exportados
 * do Postgres local do Compilações, ver `fixtures.ts`); as datas são deslocadas
 * para o "agora" de quem abre a demo, para o feed ter "Hoje" e "Ontem".
 *
 * As regras do contrato são aplicadas aqui de verdade — motivo obrigatório só ao reprovar
 * (422), mesmo status (409), não despromover a versão do painel (409), só homologadas v2.* no
 * painel (409) — com o mesmo formato de erro (`{"detail": ...}`) da API, para
 * a tela exercitar o caminho de erro real. Escritas valem só em memória.
 */

const USUARIO_DEMO = 'demo@nasajon.com.br';

// As fixtures (~100 KB de versões reais) entram por import dinâmico: viram um
// chunk à parte que o build de produção (modo `rest`) nunca baixa.
let estado!: EstadoDemo;
let carregamento: Promise<void> | null = null;

function carregarEstado(): Promise<void> {
  carregamento ??= import('./fixtures').then((modulo) => {
    estado = modulo.criarEstadoInicial();
  });
  return carregamento;
}

/** Só para testes: volta às fixtures originais na próxima chamada. */
export function resetDemoDataSource(): void {
  carregamento = null;
  resetDemoAdmin();
  resetDemoVisibilidade();
}

function esperar(signal?: AbortSignal): Promise<void> {
  const ms = config.demoLatenciaMs;
  return new Promise((resolver, rejeitar) => {
    if (signal?.aborted) return rejeitar(new DOMException('Abortado', 'AbortError'));
    if (ms <= 0) return resolver();
    const id = setTimeout(resolver, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(id);
      rejeitar(new DOMException('Abortado', 'AbortError'));
    });
  });
}

async function simular<T>(signal: AbortSignal | undefined, gerar: () => T): Promise<T> {
  return trackRequest(async () => {
    await carregarEstado();
    await esperar(signal);
    // Cópia profunda: a tela não pode mutar o "banco" por referência.
    return structuredClone(gerar());
  });
}

function erro(recurso: string, status: number, detail: string): CompilacoesHttpError {
  return new CompilacoesHttpError(recurso, status, JSON.stringify({ detail }));
}

function versaoNoPainel(): string | null {
  return estado.painel[0]?.versao ?? null;
}

function resumoDe(build: BuildDemo): BuildResumo {
  const { artefatos, eventos, ...resto } = build;
  void eventos;
  return {
    ...resto,
    // Como a API: quem não é @nasajon.com.br não recebe nada do Jenkins (URL, job e build).
    jenkins: colaboradorDoDemo() ? resto.jenkins : null,
    total_artefatos: artefatos.length,
    no_painel: build.versao === versaoNoPainel(),
    ...agregadoDaVersao(estado, build.versao, build.produto),
  };
}

function anteriorDe(build: BuildDemo): BuildDemo | null {
  const mesmos = estado.builds
    .filter((item) => item.produto === build.produto && item.branch === build.branch)
    .filter((item) => item.publicado_em < build.publicado_em);
  return mesmos.sort((a, b) => b.publicado_em.localeCompare(a.publicado_em))[0] ?? null;
}

function detalheDe(build: BuildDemo): BuildDetalhe {
  const resumo = resumoDe(build);
  const anterior = anteriorDe(build);
  return {
    ...resumo,
    artefatos: build.artefatos,
    eventos: [...build.eventos].sort((a, b) => b.criado_em.localeCompare(a.criado_em)),
    diff_anterior: calcularDiff(build.artefatos, anterior?.artefatos ?? null, anterior?.versao ?? null),
    incidentes: incidentesDaVersao(build),
    elegivel_painel: elegivelParaPainel(build)
      ? { ok: true, motivo: null }
      : { ok: false, motivo: 'Só versões homologadas de branch v2.* (formato 2.RRRR.N.0) vão para o painel.' },
  };
}

function incidentesDaVersao(build: BuildDemo): IncidenteResumo[] {
  const afetam = estado.incidentes.filter(
    (item) => item.produto === build.produto && item.versoes.some((v) => v.versao === build.versao),
  );
  return ordenarParaVersao(
    afetam.map(({ descricao, versoes, eventos, ...resto }) => {
      void descricao;
      void eventos;
      return { ...resto, total_versoes: versoes.length };
    }),
  );
}

function acharBuild(versao: string, produto = PRODUTO_PADRAO): BuildDemo {
  const build = estado.builds.find((item) => item.versao === versao && item.produto === produto);
  if (!build) throw erro(`a versão ${versao}`, 404, `Versão ${versao} não encontrada.`);
  return build;
}

function novoEvento(parcial: Omit<EventoBuild, 'id' | 'criado_em' | 'usuario'>): EventoBuild {
  estado.proximoEventoId += 1;
  return { id: estado.proximoEventoId, usuario: USUARIO_DEMO, criado_em: new Date().toISOString(), ...parcial };
}

function painelAtual(): Painel {
  const atual = estado.painel[0] ?? null;
  const build = atual ? (estado.builds.find((item) => item.versao === atual.versao) ?? null) : null;
  return {
    modo: 'local',
    versao_atual: atual?.versao ?? null,
    atualizado_em: atual?.criado_em ?? null,
    build: build ? resumoDe(build) : null,
    historico: estado.painel,
  };
}

const LIMITE_PADRAO = 30;

const demoIncidentes = proteger(criarIncidentesDemo({ estado: () => estado, simular }), 'incidentes', 'os incidentes');
const demoVisibilidade = criarVisibilidadeDemo({ estado: () => estado, simular });

export const demoDataSource: CompilacoesDataSource = {
  kind: 'demo',
  ...demoAdmin,
  ...demoIncidentes,
  ...demoVisibilidade,

  async me(signal) {
    const roles = await getAuthPort().getRoles();
    return simular(signal, () => ({
      usuario: 'demo@nasajon.com.br',
      nome: 'Sessão de demonstração',
      capabilities: config.demoCapabilities,
      colaborador: colaboradorDoDemo(),
      acesso: acessoDemo(roles),
    }));
  },

  resumo(signal) {
    return simular(signal, () => {
      const corte = Date.now() - 24 * 60 * 60 * 1000;
      return {
        total_builds: estado.builds.length,
        ultimas_24h: estado.builds.filter((item) => new Date(item.publicado_em).getTime() >= corte).length,
        nao_homologadas: estado.builds.filter((item) => item.status === 'nao_homologado').length,
        incidentes_abertos: estado.incidentes.filter((item) => item.status !== 'resolvido').length,
        painel: { versao: versaoNoPainel(), desde: estado.painel[0]?.criado_em ?? null },
      };
    });
  },

  async listarBuilds(filtros, cursor, signal) {
    await exigirAcesso('feed', 'o feed de versões');
    const ramoVisivel = await visibilidadeDeRamos();
    return simular(signal, () => {
      const termo = filtros.q?.trim().toLowerCase() ?? '';
      const filtrados = estado.builds
        .filter((item) => ramoVisivel(item.produto, item.ramo))
        .filter((item) => !filtros.produto || item.produto === filtros.produto)
        .filter((item) => !filtros.publicado_de || diaCivilSP(item.publicado_em) >= filtros.publicado_de)
        .filter((item) => !filtros.publicado_ate || diaCivilSP(item.publicado_em) <= filtros.publicado_ate)
        .filter((item) => !filtros.ramo || item.ramo === filtros.ramo)
        .filter((item) => !filtros.branch || item.branch === filtros.branch)
        .filter((item) => !filtros.status?.length || filtros.status.includes(item.status))
        .filter((item) => !termo || item.versao.toLowerCase().includes(termo))
        .filter(
          (item) =>
            !filtros.com_incidente || agregadoDaVersao(estado, item.versao, item.produto).incidentes_abertos > 0,
        )
        .sort((a, b) => b.publicado_em.localeCompare(a.publicado_em) || b.id - a.id);
      const limite = Math.min(Math.max(filtros.limit ?? LIMITE_PADRAO, 1), 100);
      const inicio = cursor ? Number(cursor) || 0 : 0;
      const pagina = filtrados.slice(inicio, inicio + limite);
      const fim = inicio + pagina.length;
      return { items: pagina.map(resumoDe), next_cursor: fim < filtrados.length ? String(fim) : null };
    });
  },

  obterBuild(versao, produto, signal) {
    return simular(signal, () => detalheDe(acharBuild(versao, produto)));
  },

  alterarStatus(versao, status, motivo, produto) {
    return simular(undefined, () => {
      const recurso = `o status da versão ${versao}`;
      const build = acharBuild(versao, produto);
      const motivoLimpo = motivo?.trim() || null;
      if (exigeMotivo(status) && !motivoLimpo) {
        throw erro(recurso, 422, `Informe o motivo da reprovação.`);
      }
      if (build.status === status)
        throw erro(recurso, 409, `A versão ${versao} já está como ${ROTULO_STATUS[status].toLowerCase()}.`);
      if (build.versao === versaoNoPainel() && build.status === 'homologado') {
        throw erro(
          recurso,
          409,
          `A versão ${versao} está no painel do cliente. Publique outra versão no painel antes de mudar o status dela.`,
        );
      }
      const anterior: StatusBuild = build.status;
      build.status = status;
      build.status_motivo = motivoLimpo;
      build.status_por = USUARIO_DEMO;
      build.status_em = new Date().toISOString();
      build.eventos.push(
        novoEvento({ tipo: 'status', status_anterior: anterior, status_novo: status, motivo: motivoLimpo }),
      );
      return detalheDe(build);
    });
  },

  async listarRamos(produto, signal) {
    await exigirAcesso('ramos', 'os ramos');
    const ramoVisivel = await visibilidadeDeRamos();
    return simular(signal, () => {
      const porRamo = new Map<string, BuildDemo[]>();
      for (const item of estado.builds) {
        if (produto && item.produto !== produto) continue;
        if (!ramoVisivel(item.produto, item.ramo)) continue;
        porRamo.set(item.ramo, [...(porRamo.get(item.ramo) ?? []), item]);
      }
      const ramos: Ramo[] = [...porRamo.entries()].map(([ramo, builds]) => {
        const ordenados = [...builds].sort((a, b) => b.publicado_em.localeCompare(a.publicado_em));
        const branches = [...new Set(ordenados.map((item) => item.branch))].sort((a, b) =>
          a === 'master' ? 1 : b === 'master' ? -1 : a.localeCompare(b),
        );
        return {
          ramo,
          branches,
          total: builds.length,
          nao_homologadas: builds.filter((item) => item.status === 'nao_homologado').length,
          homologados: builds.filter((item) => item.status === 'homologado').length,
          reprovados: builds.filter((item) => item.status === 'reprovado').length,
          ultima_versao: ordenados[0]?.versao ?? null,
          ultima_publicacao: ordenados[0]?.publicado_em ?? null,
          ultima_homologada: ordenados.find((item) => item.status === 'homologado')?.versao ?? null,
        };
      });
      return ramos.sort((a, b) => b.ramo.localeCompare(a.ramo));
    });
  },

  async obterPainel(signal) {
    await exigirAcesso('painel', 'o painel do cliente');
    return simular(signal, painelAtual);
  },

  async publicarPainel(versao) {
    await exigirAcesso('painel', 'a publicação no painel do cliente');
    return simular(undefined, () => {
      const recurso = 'a publicação no painel do cliente';
      const build = acharBuild(versao);
      if (!elegivelParaPainel(build)) {
        throw erro(recurso, 409, 'Só versões homologadas de branch v2.* (formato 2.RRRR.N.0) vão para o painel.');
      }
      const anterior = versaoNoPainel();
      if (anterior === versao) throw erro(recurso, 409, `A versão ${versao} já é a versão atual do painel.`);
      const publicacao: PublicacaoPainel = {
        versao,
        versao_anterior: anterior,
        usuario: USUARIO_DEMO,
        criado_em: new Date().toISOString(),
      };
      estado.painel = [publicacao, ...estado.painel];
      build.eventos.push(novoEvento({ tipo: 'painel', status_anterior: null, status_novo: null, motivo: null }));
      return painelAtual();
    });
  },
};
